import { Option } from '../types/option'

const FALLBACK_TIME_ZONE = 'Europe/Bucharest'

/** The browser's IANA time zone (e.g. "Europe/Bucharest"), or the app default when it cannot be resolved. */
export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIME_ZONE
  } catch {
    return FALLBACK_TIME_ZONE
  }
}

/** Current UTC offset of a zone as "UTC+03:00" ("UTC" for zero), or '' when the browser cannot format it. */
const utcOffset = (timeZone: string): string => {
  try {
    const offset = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value
    return offset?.replace('GMT', 'UTC') ?? ''
  } catch {
    return ''
  }
}

// Not in this TypeScript version's lib yet; available in every current browser.
type IntlWithSupportedValues = typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }

let allTimeZones: string[] | null = null

/** Every IANA time zone the browser knows, plus UTC and the browser's own zone. */
const listTimeZones = (): string[] => {
  if (!allTimeZones) {
    let zones: string[] = []
    try {
      zones = (Intl as IntlWithSupportedValues).supportedValuesOf?.('timeZone') ?? []
    } catch {
      // older browser: only the zones below
    }
    allTimeZones = Array.from(new Set([...zones, browserTimeZone(), FALLBACK_TIME_ZONE, 'UTC'])).sort()
  }
  return allTimeZones
}

/**
 * Options for a time zone dropdown, labelled with the current offset ("Europe/Bucharest (UTC+03:00)"). Zones the
 * browser lists under another name (e.g. a club saved as "Europe/Kiev") are kept so the saved value stays selected.
 */
export const timeZoneOptions = (...extra: (string | undefined)[]): Option[] => {
  const zones = listTimeZones()
  const missing = extra.filter((zone): zone is string => Boolean(zone) && !zones.includes(zone!))
  return [...zones, ...missing].sort().map(zone => {
    const offset = utcOffset(zone)
    return { value: zone, label: offset ? `${zone.replaceAll('_', ' ')} (${offset})` : zone.replaceAll('_', ' ') }
  })
}
